import tenantData from '@data';
import tenantUtils from '@utils';
import tenantConstants from '@constants';
import { COMMA_SPLIT } from '../../../constants/dynamicFields';
import { getDynamicDisplaySortOrder } from '../../../helpers/dynamicDisplayOrder';
import { t } from 'i18next';
import tenantTheme from '@theme';
import store from '@store';
import { capitalizeFirstLetter, resolveCreditsRequired } from '../../../utility/utility';
import { isLandCategorySlug } from '../data/listingCategories/common/landCategory';
import { isUnifiedProfolioEnabled } from '../../../utility/user';

/** Surge nests unified Oman applicable-products / credits under bayut, not unified. */
const UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY = 'bayut';

/** Surge my-listings status tab filter (Bayut KSA, Oman unified, other Surge tenants). */
export const SURGE_LISTING_STATUS_FILTER_KEY = 'f[nested.platform_listings.status.slug]';

/** @deprecated Legacy URL/API key; remapped to {@link SURGE_LISTING_STATUS_FILTER_KEY}. */
export const LEGACY_LISTING_STATUS_FILTER_KEY = 'q[status_id_eq]';

const getListingPlatformPayload = (listing, platformSlug) => {
  if (platformSlug === 'unified' && isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' })) {
    return listing?.platforms?.unified ?? listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY];
  }
  return listing?.platforms?.[platformSlug];
};

/** Unified applicable-products/credits use the bayut block; ignore API `unified` key. */
const resolveUnifiedApplicableProductsBlock = (platformWiseData) => {
  if (!platformWiseData) return null;
  return platformWiseData[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY] ?? null;
};

const preserveDurationMapOrScalar = (value, defaultExpiryDays) => {
  if (value == null) return undefined;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  return resolveCreditsRequired(value, defaultExpiryDays);
};

const normalizeApplicableProduct = (product) => ({
  ...product,
  product_id: product?.product_id ?? product?.id,
  product_title: product?.product_title ?? product?.title,
  product_title_l1: product?.product_title_l1 ?? product?.title_l1,
  product_usage_type: product?.product_usage_type ?? product?.usage_type,
  credits_required:
    product?.credits_required ??
    preserveDurationMapOrScalar(product?.required_quantity, product?.default_expiry_days),
  quantity_required:
    product?.quantity_required ??
    preserveDurationMapOrScalar(product?.required_quantity, product?.default_expiry_days),
  product_price:
    product?.product_price ?? preserveDurationMapOrScalar(product?.price, product?.default_expiry_days),
});

const normalizeApplicablePlatformBlock = (platformData) => {
  if (!platformData || typeof platformData !== 'object') return null;
  return {
    ...platformData,
    products: (platformData?.products || []).map(normalizeApplicableProduct),
    available_credits: platformData?.available_credits ?? platformData?.credits?.available,
  };
};

/** Normalizes applicable_products API: unified reads bayut; non-unified keeps bayut + dubizzle. */
export const normalizeApplicableProductsResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;

  const isUnifiedPostListing = isUnifiedProfolioEnabled({ featureKey: 'postListing' });
  const normalized = {};

  // Iterate the response keys rather than tenantData.platformList: a tenant's platform slug and the
  // applicable_products block key can differ (Bayut KSA's platform slug is `ksa` but the API keys the
  // block `bayut`). Driving this off platformList silently dropped the `bayut` block, so the upsell
  // queryFn returned `ksa: null` and the KSA upgrade page rendered blank. `unified` is handled below
  // and `success` passes through unchanged.
  Object.keys(raw).forEach((key) => {
    if (key === 'success') {
      normalized.success = raw.success;
      return;
    }
    if (key === 'unified') return;
    const block = normalizeApplicablePlatformBlock(raw[key]);
    normalized[key] = block ?? raw[key];
  });

  if (isUnifiedPostListing) {
    const unifiedBlock = normalizeApplicablePlatformBlock(raw[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY]);
    if (unifiedBlock) {
      normalized.unified = unifiedBlock;
    }
  } else if (raw.unified) {
    normalized.unified = normalizeApplicablePlatformBlock(raw.unified);
  }

  return normalized;
};

// Completion status can be a plain slug string or an object ({ slug, value }); normalize to a slug.
const getCompletionStatusSlug = (listing) => {
  const cs = listing?.completion_status ?? listing?.dynamic_data?.dynamic_fields?.completion_status;
  if (cs == null) return null;
  return typeof cs === 'string' ? cs : cs?.slug ?? null;
};

export const postedToBayut = (e) => {
  switch (e) {
    case 'not_listed':
      return false;
    case undefined:
      return false;
    case 'limit':
      return false;
    default:
      return true;
  }
};

const getSummaryData = (data, statuses) => {
  const arr = Object.keys(data)?.length > 0 ? Object.keys(data) : [];
  const stats = {};
  arr.forEach((e) => {
    const status = statuses?.find((it) => it.slug == e);
    stats[capitalizeFirstLetter(e)] = {
      title: status?.name,
      mapping: status?.id,
      total: data[e],
    };
  });
  return stats;
};

/** Merged product info for my-listings cards/table (unified + per-platform). */
export const getMergedListingProductsInfo = (listing) => {
  const isUnifiedListings = isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' });
  if (isUnifiedListings) {
    return (
      listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY]?.productsInfo ||
      listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY]?.products_information ||
      listing?.platforms?.unified?.productsInfo ||
      listing?.platforms?.unified?.products_information ||
      {}
    );
  }
  return (
    listing?.property?.productsInfo ||
    listing?.platforms?.bayut?.productsInfo ||
    listing?.platforms?.bayut?.products_information ||
    listing?.platforms?.dubizzle?.productsInfo ||
    listing?.platforms?.dubizzle?.products_information ||
    {}
  );
};

/**
 * Active listing product tier for chip display; order matches change-ownership modal / quota UI.
 */
export const getAppliedListingProductSlugFromProductsInfo = (productsInfo) => {
  const pi = productsInfo;
  if (!pi || typeof pi !== 'object') return null;
  if (pi['signature-listing']?.is_applied) return 'signature-listing';
  if (pi['hot-listing']?.is_applied) return 'hot-listing';
  if (pi['basic-listing-dubizzle']?.is_applied) return 'basic-listing-dubizzle';
  if (pi['basic-listing']?.is_applied) return 'basic-listing';
  return null;
};

export const platformMapper = (e, user, platform, setStatsLoading = true, existingStats) => {
  let platformListingId;
  if (!user?.isMultiPlatform) {
    const possiblePlatforms = ['unified', 'bayut', 'dubizzle'] // replace it with Object.keys in future and check if platform_listing_id is present in any key, store it.
    possiblePlatforms?.forEach((gccPlatform) => {
      if (e?.platforms?.[gccPlatform]) {
        platformListingId = e?.platforms?.[gccPlatform]?.platform_listing_id;
      }
    });
  }

  const platformSlug = platform?.slug;
  const platformPayload = getListingPlatformPayload(e, platformSlug);
  const platformPublicUrl =
    tenantUtils.getLocalisedString(platformPayload, 'url') ||
    tenantUtils.getLocalisedString(
      {
        url: e?.url?.[platformSlug],
        url_l1: e?.url_l1?.[platformSlug],
      },
      'url',
    );

  const useStatsLoadingSkeleton =
    setStatsLoading &&
    !(platformSlug === 'unified' && isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' }));

  const platformStatusSlug = platformPayload?.status?.slug ?? e?.status?.slug;

  return {
    id: e.id,
    platform_listing_id: platformListingId, // e?.platforms?.[platform?.slug]?.platform_listing_id,
    property_id: e?.id,
    slug: platform?.slug,
    ...(useStatsLoadingSkeleton && { stats: { views: 'loading', clicks: 'loading', leads: 'loading' } }),
    ...(!setStatsLoading && existingStats && { stats: existingStats?.platforms?.[platform?.slug]?.stats }),
    ...(platformSlug === 'unified' &&
      isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' }) &&
      setStatsLoading && {
        stats: { views: 0, clicks: 0, leads: 0 },
      }),
    showPostedAt: platformPayload?.status?.slug === 'active',
    postedOn: platformPayload?.posted_at,
    expiryDate: platformPayload?.expires_at,
    status: tenantUtils.listingStatusMapper(platformStatusSlug),
    public_url: platformPublicUrl,
    disposition: tenantUtils.listingDispositionMapper(platformPayload?.disposition),
    location: { ...e.location, breadcrumb: e?.location?.breadcrumb },
    hidePostNowBtn:
      platformPayload?.status?.slug === 'active' ||
      platformPayload?.status?.slug === 'pending' ||
      platformPayload?.disposition?.slug === 'rejected',
    showStats:
      platformPayload?.status?.slug === 'active' ||
      platformPayload?.disposition?.slug === 'changes-in-review',
    icon: platform?.icon,
    listingPlatformActions: tenantUtils.getListingPlatformActions(e, user, platform?.slug),
    isBadge:
      platformSlug === 'bayut' || platformSlug === 'unified'
        ? (platformPayload ?? e?.platforms?.bayut)?.products_information?.['hot-listing']?.is_applied
        : e?.platforms?.dubizzle?.products_information?.['feature']?.is_applied,
    productsInfo: platformPayload?.products_information,
    ...(!!e?.user && { listingOwner: e?.user }),
    user: user,
  };
};

const pickPlatformsAutoRenewableItem = (listing) => {
  const unifiedPlatform =
    listing?.platforms?.unified ??
    (isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' })
      ? listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY]
      : null);
  const nested =
    unifiedPlatform?.products_information?.['basic-listing']?.auto_renewable_item ||
    unifiedPlatform?.products_information?.['basic-listing-dubizzle']?.auto_renewable_item;
  const merged = nested && typeof nested === 'object' ? { ...nested } : {};
  const isApplied =
    !!listing?.is_autorenewal_on ||
    merged?.isApplied === true ||
    merged?.is_applied === true ||
    !!merged?.id ||
    !!merged?.auto_renewable_id;
  return {
    ...merged,
    isApplied,
    id: merged?.id,
    renewing_on: merged?.renewing_on,
  };
};

export const listingMapper = (listing, user, setStatsLoading, existingStats = {}) => {
  const platforms = {
    property_id: listing?.id,
    ...(listing?.listing_purpose && {
      purpose: listing?.listing_purpose?.title,
      purposeId: listing?.listing_purpose?.id,
    }),
    expiryDays: listing?.expiry_days || 30,
    isMultiPlatform: user?.isMultiPlatform,
    listingRowActions: (refsObject, loading) => {
      const actions = tenantUtils.getListingRowActions(listing, refsObject, loading, user);
      return actions;
    },
    ...(user?.platforms || []).reduce((acc, platform) => {
      if (platform?.slug) {
        acc[platform.slug] = platformMapper(listing, user, platform, setStatsLoading, existingStats);
      }
      return acc;
    }, {}),
    ...(isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' }) &&
    (listing?.platforms?.unified || listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY])
      ? {
          unified: platformMapper(
            listing,
            user,
            { slug: 'unified', icon: 'BayutSmallLogo' },
            setStatsLoading,
            existingStats,
          ),
        }
      : listing?.platforms?.unified
        ? {
            unified: platformMapper(
              listing,
              user,
              { slug: 'unified', icon: 'BayutSmallLogo' },
              setStatsLoading,
              existingStats,
            ),
          }
        : {}),
    auto_renewable_item: pickPlatformsAutoRenewableItem(listing),
  };
  const mergedProductsInfo = getMergedListingProductsInfo(listing);
  const appliedListingProductSlug = getAppliedListingProductSlugFromProductsInfo(mergedProductsInfo);
  return {
    ...listing,
    id: listing?.id,
    property: {
      id: listing?.id,
      ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
      type: listing?.listing_type,
      // Completion status may arrive as a string ('ready') or an object ({ slug: 'ready' }).
      // Land listings never surface Ready / Off-Plan (matches Bayut KSA behaviour).
      completion_status: isLandCategorySlug(listing?.listing_type?.slug ?? listing?.listing_category?.slug)
        ? null
        : getCompletionStatusSlug(listing),
      projectStatus: getCompletionStatusSlug(listing) === 'ready' ? 'Ready' : 'Off-Plan',
      assignedTo: (() => {
        if (listing?.user) {
          const name = tenantUtils.getLocalisedString(listing.user, 'name');
          return name || '—';
        }
        return listing?.assigned_to || '—';
      })(),
      area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.name },
      location: {
        ...listing?.location,
        breadcrumb: listing?.location?.breadcrumb
          ?.filter((e) => e?.level > 1)
          ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
          ?.join(', '),
      },
      // Prefer flattened listing.image from normalizeSurgeListingsResponse (sizes.thumbnail → thumbnail).
      image: listing?.image,
      imageCount: listing?.images_count ?? listing?.images?.length ?? 0,
      price: { value: listing?.price, currency: listing?.currency },
      details: {
        listingSpecs: tenantData?.getListingSpecs(listing),
        listing_type: listing?.listing_type,
      },
      ...(listing?.health && {
        health: listing?.health,
      }),
      showHealth: true,
      productsInfo: mergedProductsInfo,
      isBadge: !!appliedListingProductSlug,
    },
    platforms,
  };
};

export const getMyListingsData = (summaryRes, listingsRes, userId) => {
  const listings = listingsRes?.listings;
  const pagination = listingsRes?.pagination;
  const summary = getSummaryData(summaryRes?.summary, listingsRes?.statuses_and_dispositions);
  const loginUser = store.getState().app.loginUser?.user;
  const isAgency = loginUser?.agency?.id;
  let user = {};
  if (isAgency) {
    user = loginUser?.agency?.users?.find((e) => e?.id == userId);
  } else {
    user = loginUser;
  }
  if (!user || userId == -1) {
    user = {
      ...loginUser,
      ...loginUser?.agency,
      name: loginUser?.agency?.name,
      name_l1: loginUser?.agency?.name_l1,
      id: -1,
      profile_image: loginUser?.agency?.agency_logo,
      is_agency_admin: true,
    };
  }
  return {
    list: listings && listings?.length ? listings?.map((item) => listingMapper(item, user)) : [],
    pagination: tenantUtils.getPaginationObject(pagination),
    tabFilterKey: SURGE_LISTING_STATUS_FILTER_KEY,
    dontAllowRowSlection: true,
    statuses_and_dispositions: listingsRes?.statuses_and_dispositions,
    statuses: summary
      ? [
          ...Object.keys(summary).map((e) => ({
            ...summary[e],
            key: e === 'All' ? '' : summary[e].mapping,
            label: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
            tab: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
          })),
        ]
      : [],
  };
};

const updateListingStats = (listing) => {
  if (listing) {
    return {
      views: listing?.sum_search_count,
      leads: listing?.sum_lead_count,
      clicks: listing?.sum_view_count,
      ctr: (listing?.sum_view_count / listing?.sum_search_count) * 100,
      calls: listing?.sum_phone_view_count,
      emails: listing?.sum_email_lead_count,
      sms: listing?.sum_sms_view_count,
      whatsapp: listing?.sum_whatsapp_view_count,
      chat: listing?.sum_chat_lead_count,
      stats: {
        views: listing?.sum_search_count,
        clicks: listing?.sum_view_count,
        leads: listing?.sum_lead_count,
      },
    };
  } else {
    return {
      views: '',
      leads: '',
      clicks: '',
      ctr: '',
      calls: '',
      emails: '',
      sms: '',
      whatsapp: '',
      chat: '',
      stats: {
        views: '',
        clicks: '',
        leads: '',
      },
      statsError: 'Bayut Stats Error',
    };
  }
};

/** Maps `GET .../surge/bi/dashboard/listing_stats` row into unified platform listing stats + lead breakdown for tooltips. */
export const mapUnifiedSurgeBiListingStats = (row) => {
  if (!row || typeof row !== 'object') {
    return {
      views: 0,
      clicks: 0,
      leads: 0,
      calls: 0,
      whatsapp: 0,
      sms: 0,
      emails: 0,
      chat: 0,
      stats: {
        views: 0,
        clicks: 0,
        leads: 0,
      },
    };
  }
  const views = row.total_impressions ?? 0;
  const clicks = row.total_detail_page_views ?? 0;
  const leads = row.total_lead_count ?? 0;
  return {
    views,
    clicks,
    leads,
    calls: row.phone_leads ?? 0,
    whatsapp: row.whatsapp_leads ?? 0,
    sms: row.sms_leads ?? 0,
    emails: row.email_leads ?? 0,
    chat: row.chat_leads ?? 0,
    stats: {
      views,
      clicks,
      leads,
    },
  };
};

export const finalizeUnifiedListingStatsPlaceholders = (draftListings) => {
  if (!isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' })) return;
  draftListings?.list?.forEach((listing) => {
    const u = listing?.platforms?.unified;
    if (!u) return;
    const s = u.stats || {};
    const hasLoading =
      s.views === 'loading' ||
      s.clicks === 'loading' ||
      s.leads === 'loading' ||
      u.views === 'loading' ||
      u.clicks === 'loading' ||
      u.leads === 'loading';
    if (!hasLoading) return;
    const z = (v) => (v === 'loading' ? 0 : v);
    listing.platforms.unified = {
      ...u,
      views: z(u.views),
      clicks: z(u.clicks),
      leads: z(u.leads),
      stats: {
        views: z(s.views),
        clicks: z(s.clicks),
        leads: z(s.leads),
      },
    };
  });
};

const getProductDetailToBeApplied = (data, productSlug) => {
  const details = data?.products?.find((it) => it?.slug == productSlug);
  if (details) {
    const creditsRequired =
      resolveCreditsRequired(details?.credits_required, 30, details?.default_expiry_days) ?? 0;
    const availableCredits = Number(data?.available_credits) || 0;
    return {
      ...tenantData.getListingActions(productSlug),
      ...details,
      isSufficient: availableCredits >= creditsRequired,
      availableCredits: data?.available_credits,
    };
  } else {
    return details;
  }
};

const getProductDetails = (allProducts) => {
  return allProducts?.length
    ? allProducts?.map((e) => {
        const productDetails = tenantData.getListingActions(e?.slug);
        if (productDetails) {
          return { ...productDetails, ...e };
        } else {
          return e;
        }
      })
    : [];
};

const upsellDataMapper = (platformWiseData, listingDetailResponse, platformProductSlugs) => {
  const user = store.getState().app.loginUser.user;
  const listing = listingDetailResponse?.listing;

  const mapMergedProducts = (platformData, slugList) =>
    (slugList
      ?.map((slug) => {
        const tenantProduct = tenantData.products.find((pr) => pr?.slug == slug);
        const resProduct = platformData?.products?.find((prod) => prod?.slug === slug);
        if (!resProduct) return null;
        return {
          ...tenantProduct,
          requiredCredits: resProduct.credits_required,
          price: resProduct.product_price,
          isRecommended: resProduct.is_recommended,
          isAddOn: resProduct.is_add_on,
          activeDuration:
            Number(listingDetailResponse?.listingExpiryDays) ||
            Number(resProduct.default_expiry_days) ||
            30,
          id: resProduct.product_id,
          title: tenantUtils.getLocalisedString(resProduct, 'product_title'),
          description: tenantUtils.getLocalisedString(resProduct, 'description'),
        };
      })
      ?.filter(Boolean) || []);

  const unifiedBlock = resolveUnifiedApplicableProductsBlock(platformWiseData);
  const useUnifiedUpsellBucket =
    isUnifiedProfolioEnabled({ featureKey: 'postListing' }) && unifiedBlock?.products?.length > 0;

  let data = {
    platforms: {},
  };

  if (useUnifiedUpsellBucket) {
    const unifiedPlatformMeta = listing?.platforms?.[UNIFIED_LISTINGS_CREDITS_PLATFORM_KEY];
    const slugList = unifiedBlock.products
      .map((p) => p?.slug)
      .filter((slug) => slug && slug !== 'basic-listing' && slug !== 'basic-listing-dubizzle');
    data.platforms.unified = {
      availableCredits: unifiedBlock.available_credits,
      creditUnitPrice: unifiedBlock.credit_unit_price,
      isPosted:
        unifiedPlatformMeta?.is_posted ?? listing?.is_posted ?? !!listingDetailResponse?.isListingPosted,
      products: mapMergedProducts(unifiedBlock, slugList),
    };
  } else {
    const defaultProductSlugs = { bayut: ['hot-listing'], dubizzle: ['feature'] };
    const toSlugArray = (value) => (Array.isArray(value) ? value : value != null ? [value] : []);

    user?.platforms?.forEach((e) => {
      const slug = e?.slug;
      const platformData = platformWiseData?.[slug];
      const listingPlatform = listingDetailResponse?.listing?.platforms?.[slug];
      if (!slug || !platformData || !listingPlatform) return;
      if (slug === 'dubizzle' && !listingPlatform.is_posted) return;
    
      const requestedSlugs = toSlugArray(platformProductSlugs?.[slug]);
      const slugRows = requestedSlugs.length ? requestedSlugs : defaultProductSlugs[slug] || [];

      data.platforms[slug] = {
        availableCredits: platformData?.available_credits,
        creditUnitPrice: platformData?.credit_unit_price,
        isPosted: !!listingPlatform.is_posted,
        products: mapMergedProducts(platformData, slugRows),
      };
    });
  }
  let totalCredits = 0;
  Object.values(data?.platforms)?.forEach((plat) => {
    if (plat?.products?.length) totalCredits = totalCredits + plat.availableCredits;
  });
  data.currency = totalCredits > 0 ? 'Credits' : tenantConstants.CURRENCY;
  data.headingMsg = 'Listing Posted';
  data.descriptionMsg = `Your listing has been successfully posted`;
  data.listing = listingDetailResponse;
  return data;
};

const listingCardMapper = (listing) => {
  listing.images = listing.images?.sort((image1, image2) => image1.order - image2.order);
  return {
    id: listing?.id,
    ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
    type: listing?.listing_type,
    area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.id || 2 },
    location: {
      ...listing?.location,
      breadcrumb: listing?.location?.breadcrumb
        ?.reverse()
        ?.filter((e) => e?.level > 1)
        ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
        ?.join(', '),
    },
    image: listing?.images?.find((item) => item?.is_main_image) || listing?.images?.[0],
    price: { value: listing?.price, currency: tenantConstants.CURRENCY },
    details: {
      ...listing?.rega_details?.property_specs,
      listingSpecs: tenantData?.getListingSpecs(listing),
      listing_type: listing?.listing_type,
      regaId: listing?.ad_license,
      regaExpiryDate: listing?.platforms?.rega_expiry_date,
    },
    ...(listing?.health && {
      health: listing?.health,
    }),
    disposition: listing?.disposition?.slug,
    permit_number: listing?.permit_number,
  };
};

export const amenitiesDataMapper = (payload) => {
  const fieldType = { number: 'input', text: 'input', checkbox: 'checkbox', select: 'select' };

  const sortIndex = { number: 3, text: 2, checkbox: 4, select: 1 };
  const amenitiesData = {};
  payload &&
    payload.forEach((amenityItem, index) => {
      if (amenityItem.groups.length) {
        Object.defineProperty(amenitiesData, amenityItem.type_id, { value: { fields: {}, fieldsSections: [] } });
      }
      amenityItem.groups.forEach((e, i) => {
        amenitiesData?.[amenityItem.type_id].fieldsSections.push({
          title: tenantUtils.getLocalisedString(e, 'title'),
          fields: [],
        });
        // to sort
        const features = e.features.sort((first, second) => {
          return sortIndex[first.format] < sortIndex[second.format]
            ? -1
            : sortIndex[first.format] == sortIndex[second.format]
              ? 0
              : 1;
        });
        features.forEach((item) => {
          amenitiesData[amenityItem.type_id].fields[item.id] = {
            type: fieldType[item.format],
            initialValue: '',
            value: '',
            props: {
              options:
                fieldType[item.format] === 'select'
                  ? tenantUtils
                      .getLocalisedString(item, 'options')
                      .split(',')
                      .map((e) => ({ id: e, name: e }))
                  : undefined,
              label: tenantUtils.getLocalisedString(item, 'title'),
              horizontal: true,
              id: item.id,
            },
          };
          amenitiesData[amenityItem.type_id].fieldsSections[i].fields.push(item.id);
        });
      });
    });
  return { data: amenitiesData };
};

/**
 * Converts the "features" field from dynamic_fields API (dynamic_field_options + dynamic_section)
 * into the amenities-style format expected by AddAmenities modal: { fields, fieldsSections }.
 * Uses dynamic_section to form groups (same structure as /api/surge/amenities groups).
 * Single source of truth: no fallback to amenities API.
 * Recognized format_type values: checkbox, select, number (numeric input).
 * @param {Object} featuresDef - The features field from dynamicFieldDefinitions: { apiKeyName, options } where each option has id, label, label_l1, value, slug, dynamic_section
 * @param {{ useSecondaryDisplayOrder?: boolean }} [modalOptions]
 * @returns {{ fields: Object, fieldsSections: Array }}
 */
export const dynamicFieldsToFeaturesModalData = (featuresDef, modalOptions = {}) => {
  const { useSecondaryDisplayOrder = false } = modalOptions;
  const fieldType = { select: 'select', checkbox: 'checkbox', number: 'input' };
  const sortIndex = { select: 1, checkbox: 2, number: 3 };
  const fields = {};
  const fieldsSections = [];

  const options = featuresDef?.options ?? [];
  if (!options.length) return { fields, fieldsSections };

  // Group options by dynamic_section (by section id), preserve section order
  const sectionMap = new Map();
  const sectionOrder = [];
  options.forEach((opt) => {
    const section = opt?.dynamic_section ?? { id: 'default', name: null, name_l1: null, slug: 'default', display_order: 999 };
    const sid = section.id ?? section.slug ?? 'default';
    if (!sectionMap.has(sid)) {
      sectionMap.set(sid, { section, optionIds: [] });
      sectionOrder.push({
        id: sid,
        display_order: getDynamicDisplaySortOrder(section, useSecondaryDisplayOrder, 999),
      });
    }
    sectionMap.get(sid).optionIds.push(opt);
  });

  sectionOrder.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));

  sectionOrder.forEach(({ id: sid }) => {
    const { section, optionIds } = sectionMap.get(sid) || {};
    if (!section || !optionIds.length) return;

    // Section title from API only (no fallbacks)
    fieldsSections.push({
      title: section.name ?? null,
      title_l1: section.name_l1 ?? null,
      fields: [],
    });
    const sectionIndex = fieldsSections.length - 1;

    // Format from API format_type when present; else derive from value
    const formatForOption = (opt) => {
      if (opt?.format_type === 'checkbox' || opt?.format_type === 'select' || opt?.format_type === 'number') {
        return opt.format_type;
      }
      if (opt?.format_type === 'string') {
        const v = opt?.value;
        if (v != null && COMMA_SPLIT.test(String(v))) return 'select';
      }
      const v = opt?.value;
      if (v == null || (typeof v === 'string' && !v.trim())) return 'checkbox';
      const trimmed = String(v).trim();
      if (COMMA_SPLIT.test(trimmed)) return 'select';
      return 'checkbox';
    };

    const sortedOptions = [...optionIds].sort((a, b) => {
      const fa = formatForOption(a);
      const fb = formatForOption(b);
      const byFormat = (sortIndex[fa] ?? 2) - (sortIndex[fb] ?? 2);
      if (byFormat !== 0) return byFormat;
      return (
        getDynamicDisplaySortOrder(a, useSecondaryDisplayOrder, 0) -
        getDynamicDisplaySortOrder(b, useSecondaryDisplayOrder, 0)
      );
    });

    sortedOptions.forEach((item) => {
      const format = formatForOption(item);
      const type = fieldType[format] ?? 'checkbox';
      const valueStr = item.value != null ? tenantUtils.getLocalisedString(item, 'value') : null;
      const optionsProp =
        type === 'select' && valueStr && String(valueStr).trim()
          ? String(valueStr)
              .split(COMMA_SPLIT)
              .map((x) => ({ id: x.trim(), name: x.trim() }))
          : undefined;
      const labelFromApi = item.label != null || item.label_l1 != null ? tenantUtils.getLocalisedString(item, 'label') : null;
      fields[item.id] = {
        type,
        initialValue: '',
        value: '',
        props: {
          options: optionsProp,
          label: labelFromApi,
          horizontal: true,
          id: item.id,
          slug: item.slug ?? null,
          ...(format === 'number' && { type: 'number' }),
        },
      };
      fieldsSections[sectionIndex].fields.push(item.id);
    });
  });

  return { fields, fieldsSections };
};

const productsDataMapper = (response) => {
  let productsByPlatform = {};
  const productList = response?.products;

  tenantData.platformList.forEach(({ key, responseKey }) => {
    productsByPlatform[key] = [];
    const platformProducts = productList?.filter((e) => e?.platform?.slug == responseKey);
    platformProducts?.forEach((product) => {
      const { product_id, slug } = product;

      productsByPlatform[key].push({
        ...product,
        ...tenantData.getListingActions(slug),
        product_id,
        price: product?.product_price,
      });
    });
  });

  return {
    platformProducts: productsByPlatform,
    products: !!productList?.length ? productList.map((e) => ({ ...tenantData.getListingActions(e?.slug), ...e })) : [],
  };
};

export const dataMapper = (platformWiseData, listingDetailResponse) => {
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });

  const data = {
    availableCredits: platformWiseData?.available_credits,
    creditUnitPrice: platformWiseData?.credit_unit_price,
    isPosted: listingDetailResponse?.isListingPosted,
    // isPosted: true,
    headingMsg: '',
    descriptionMsg: '',
    currency: '',
    applicableProducts: [],
    subServices: [],
    serviceStates: {},
    autoRenewState: {},
    listing: listingDetailResponse?.listing,
  };

  data?.availableCredits > 0 ? (data.currency = 'Credits') : (data.currency = t(tenantConstants.CURRENCY));

  platformWiseData?.products.forEach((item) => {
    if (item.is_add_on === false && item.slug !== 'refresh') {
      data.applicableProducts.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        isAddOn: item.is_add_on,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.autoRenewState[item.product_id] = false;
    } else if (item.is_add_on === true) {
      data.subServices.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.serviceStates[item.product_id] = false;
    }
  });
  data.headingMsg = listingDetailResponse?.isListingPosted
    ? 'Your Listing has been Posted Successfully!'
    : 'Almost There';
  data.descriptionMsg = listingDetailResponse?.isListingPosted
    ? 'Make your Listing Hot and reach 10x more Buyers'
    : 'Choose the type of listing you want to publish';

  return data;
};

export const mapPlatformData = (res, platforms, listingDetailResponse) => {
  const data = {};
  platforms?.forEach((platform) => {
    data[platform?.slug] = dataMapper(res, listingDetailResponse);
  });
  return data;
};

const widgetParser = (listings, platform) => {
  const productsToShow = {
    bayut: [{ slug: 'signature-listing' }, { slug: 'hot-listing' }, { slug: 'basic-listing' }],
    dubizzle: [{ slug: 'basic-listing-dubizzle' }, { slug: 'feature' }, { slug: 'boost-to-top' }],
  };
  const data = {
    [platform]: {
      title: 'Listings',
      icon_data: {},
      link_data: {
        text: 'View All Listings',
        to: '/listings',
      },
      total_title: 'Active',
      total_title_icon: 'MdCircle',
      total_value: '',
      purposes: [
        {
          id: 1,
          icon: 'IconForSale',
          iconProps: { color: tenantTheme['color-for-sale'], hasBackground: true },
          title: 'For Sale',
          value: '',
        },
        {
          id: 2,
          icon: 'IconForRent',
          iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
          title: 'To Rent',
          value: '',
        },
        // {
        //   id: 4,
        //   icon: 'IconRental',
        //   iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
        //   title: 'Daily Rentals',
        //   value: '',
        // },
      ],
      products: tenantUtils.generateProductData(productsToShow?.[platform]),
    },
  };

  const currentPlatform = data?.[platform];
  currentPlatform.total_value = listings?.active;
  currentPlatform.purposes[0].value = listings?.sale;
  currentPlatform.purposes[1].value = listings?.rent;
  // currentPlatform.purposes[2].value = listings?.daily_rental;
  currentPlatform.products.forEach((item, i) => {
    currentPlatform.products[i].value = listings?.[item.slug_alt];
  });

  return data?.[platform];
};

const getListingSummaryStats = (response, platform) => {
  return { [platform]: widgetParser(response?.data?.stats?.platforms?.[platform], platform) };
};

export const listingDetailMapper = (listing, platforms) => {
  if (listing?.project_info?.id) {
    return {
      ...listing,
      id: listing?.id,
      location: {
        ...listing?.location,
        breadcrumbs: listing?.location?.breadcrumbs,
      },
      images: listing?.images || [],
      image: listing?.images?.find((img) => img?.is_main_image) || listing?.images?.[0],
      total_units: listing?.total_units,
      under_construction: listing?.under_construction,
      project_info: listing?.project_info,
      assignedTo:
        tenantUtils.getLocalisedString(listing?.user, 'name') ||
        listing?.assigned_to ||
        undefined,
    };
  }

  return {
    id: listing?.id,
    permit_number: listing?.permit_number,
    ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
    isDailyRental: listing?.listing_purpose?.slug == 'daily-rental',
    isOffPlan: listing?.is_offplan_listing || null,
    projectStatus: listing?.completion_status === 'ready' ? 'Ready' : 'Off-Plan',
    saleType: listing?.sale_type,
    floorPlan: (listing?.unit_variant?.floor_plan?.length ?? 0) > 0,
    completion_status: listing?.completion_status,
    type: listing?.listing_type,
    area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.name },
    location: {
      ...listing?.location,
      breadcrumb: listing?.location?.breadcrumb
        ?.reverse()
        ?.filter((e) => e?.level > 1)
        ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
        ?.join(', '),
    },
    image:
      listing?.image == null
        ? listing?.image
        : typeof listing?.image === 'string'
          ? { thumbnail: listing.image }
          : listing?.image,
    imageCount: listing?.images_count,
    price: {
      value: listing?.price,
      currency: listing?.currency || tenantConstants.CURRENCY,
    },
    details: {
      ...listing?.rega_details?.property_specs,
      listingSpecs: tenantData?.getListingSpecs(listing),
      listing_type: listing?.listing_type,
      regaId: listing?.ad_license,
      regaExpiryDate: listing?.platforms?.rega_expiry_date,
      permit_number: listing?.permit_number,
    },
    ...(listing?.health && {
      health: listing?.health,
    }),
    showHealth: true,
    productsInfo:
      listing?.platforms?.ksa?.products_information ??
      listing?.platforms?.bayut?.products_information ??
      listing?.platforms?.dubizzle?.products_information,
    isBadge: !!(
      listing?.platforms?.ksa?.products_information?.['basic-listing']?.is_applied ||
      listing?.platforms?.bayut?.products_information?.['basic-listing']?.is_applied ||
      listing?.platforms?.dubizzle?.products_information?.['basic-listing-dubizzle']?.is_applied
    ),
    status: listing?.status?.slug,
    platforms: platforms ?? listing?.platforms,
    url: tenantUtils.getLocalisedString(listing, 'url'),
    assignedTo:
      tenantUtils.getLocalisedString(listing?.user, 'name') || listing?.assigned_to || undefined,
    ...(tenantUtils.getLocalisedString(listing, 'title')
      ? { listingTitle: tenantUtils.getLocalisedString(listing, 'title') }
      : {}),
  };
};
export default {
  getMyListingsData,
  productsDataMapper,
  mapPlatformData,
  listingMapper,
  platformMapper,
  normalizeApplicableProductsResponse,
  amenitiesDataMapper,
  dynamicFieldsToFeaturesModalData,
  updateListingStats,
  mapUnifiedSurgeBiListingStats,
  finalizeUnifiedListingStatsPlaceholders,
  getProductDetailToBeApplied,
  getProductDetails,
  postedToBayut,
  upsellDataMapper,
  getListingSummaryStats,
  listingCardMapper,
  listingDetailMapper,
};
